import { useCallback, useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SideNavLayout from "../Sidenav/SideNavLayout";
import api from "../api/api";

const ACTION_STYLES = {
  LOGIN: "bg-green-100 text-green-700",
  LOGOUT: "bg-gray-200 text-gray-700",
  PAGE_VIEW: "bg-blue-100 text-blue-700",
  CLICK: "bg-slate-100 text-slate-600",
  CREATE: "bg-emerald-100 text-emerald-700",
  EDIT: "bg-amber-100 text-amber-700",
  DELETE: "bg-red-100 text-red-700",
};

const ACTION_LABELS = {
  LOGIN: "Login",
  LOGOUT: "Logout",
  PAGE_VIEW: "Page View",
  CLICK: "Click",
  CREATE: "Create",
  EDIT: "Edit",
  DELETE: "Delete",
};

const PAGE_SIZE = 50;

const ActivityTrail = () => {
  const [loader, setLoader] = useState(false);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  // Filters
  const [searchText, setSearchText] = useState("");
  const [action, setAction] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setLoader(true);
      const params = { page, page_size: PAGE_SIZE };
      if (searchText.trim()) params.search = searchText.trim();
      if (action) params.action = action;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const response = await api.get("/v1/activity/logs", { params });
      if (response?.status === 200) {
        setLogs(response.data || []);
        setTotal(response.total || 0);
      } else {
        setLogs([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Failed to fetch activity logs:", error);
      setLogs([]);
      setTotal(0);
    } finally {
      setLoader(false);
    }
  }, [page, searchText, action, fromDate, toDate]);

  useEffect(() => {
    const timer = setTimeout(fetchData, searchText ? 400 : 0); // debounce search
    return () => clearTimeout(timer);
  }, [fetchData, searchText]);

  // Reset to first page whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [searchText, action, fromDate, toDate]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const formatTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    return isNaN(d.getTime()) ? value : d.toLocaleString();
  };

  return (
    <SideNavLayout>
      {loader && (
        <div className="fixed inset-0 bg-white/70 flex justify-center items-center z-[9999]">
          <CircularProgress size={60} />
        </div>
      )}

      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl text-start font-semibold text-[#1d2951]">
            Activity Trail
          </h1>
          <button
            onClick={fetchData}
            className="flex items-center gap-1 border border-gray-300 px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
            title="Refresh"
          >
            <RefreshIcon style={{ fontSize: 18 }} /> Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-center p-3 border-b">
          <select
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="border border-gray-300 px-4 py-2 rounded-lg text-sm"
          >
            <option value="">All Actions</option>
            {Object.entries(ACTION_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="border border-gray-300 px-4 py-2 rounded-lg text-sm"
            title="From date"
          />
          <span className="text-gray-400 text-sm">to</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="border border-gray-300 px-4 py-2 rounded-lg text-sm"
            title="To date"
          />

          <input
            type="text"
            placeholder="Search by user, screen or action target"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="border border-gray-300 px-4 py-2 rounded-lg text-sm w-72 ml-auto"
          />
        </div>

        {/* Table Card */}
        <div className="bg-white rounded-xl shadow-sm border mt-4">
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 text-center text-gray-600 uppercase text-xs sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3">S/N</th>
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Action</th>
                  <th className="px-4 py-3">Screen</th>
                  <th className="px-4 py-3">Target</th>
                  <th className="px-4 py-3">Time</th>
                </tr>
              </thead>

              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-10 text-center text-gray-500">
                      No activity found.
                    </td>
                  </tr>
                ) : (
                  logs.map((log, index) => (
                    <tr key={log.id} className="border-t text-center">
                      <td className="px-4 py-3">
                        {(page - 1) * PAGE_SIZE + index + 1}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {log.user_name || "-"}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{log.role || "-"}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                            ACTION_STYLES[log.action] || "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {ACTION_LABELS[log.action] || log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{log.page || "-"}</td>
                      <td
                        className="px-4 py-3 text-gray-600 max-w-[220px] truncate"
                        title={log.target || ""}
                      >
                        {log.target || "-"}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {formatTime(log.created_on)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t text-sm text-gray-600">
            <span>
              {total} event{total === 1 ? "" : "s"} · Page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="border border-gray-300 px-3 py-1.5 rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="border border-gray-300 px-3 py-1.5 rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </SideNavLayout>
  );
};

export default ActivityTrail;
